import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { ScanEye, Mail, Shield, Zap } from "lucide-react";
import { useTranslation } from "react-i18next";

export default function About() {
  const { t } = useTranslation();

  const cards = [
    { icon: Shield, title: t("about.mission.title"), desc: t("about.mission.desc") },
    { icon: Zap,    title: t("about.tech.title"),    desc: t("about.tech.desc")    },
    { icon: Mail,   title: t("about.contact.title"), desc: t("about.contact.desc") },
  ];

  const whyItems = [
    t("about.why.f1"), t("about.why.f2"), t("about.why.f3"),
    t("about.why.f4"), t("about.why.f5"),
  ];

  return (
    <main className="min-h-screen" data-testid="about-page">
      <div className="max-w-3xl mx-auto px-4 md:px-8 py-16">
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
          <span className="text-xs font-mono uppercase tracking-widest text-muted-foreground">{t("about.tag")}</span>
          <h1 className="font-heading text-4xl md:text-5xl font-bold tracking-tight mt-2 mb-8">{t("about.title")}</h1>

          <div className="prose prose-sm max-w-none space-y-6 text-muted-foreground">
            <p className="text-base text-foreground leading-relaxed">{t("about.desc")}</p>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 my-8">
              {cards.map(({ icon: Icon, title, desc }) => (
                <div key={title} className="p-5 rounded-md border border-border bg-card">
                  <Icon className="w-5 h-5 text-brand-primary mb-3" />
                  <h3 className="font-heading font-semibold mb-2">{title}</h3>
                  <p className="text-xs text-muted-foreground leading-relaxed">{desc}</p>
                </div>
              ))}
            </div>

            <h2 className="font-heading text-xl font-semibold text-foreground">{t("about.why.title")}</h2>
            <ul className="space-y-2 text-sm">
              {whyItems.map((item) => (
                <li key={item} className="flex items-start gap-2">
                  <div className="w-1.5 h-1.5 rounded-full bg-brand-primary mt-1.5 flex-shrink-0" />
                  {item}
                </li>
              ))}
            </ul>

            <div id="api" className="p-5 rounded-md border border-border bg-card mt-8">
              <h3 className="font-heading font-semibold mb-2">{t("about.api.title")}</h3>
              <p className="text-xs text-muted-foreground">{t("about.api.desc")}</p>
            </div>

            <div id="contact" className="p-5 rounded-md border border-border bg-card">
              <h3 className="font-heading font-semibold mb-2">{t("about.contactUs.title")}</h3>
              <p className="text-xs text-muted-foreground">{t("about.contactUs.email")}</p>
              <p className="text-xs text-muted-foreground mt-1">{t("about.contactUs.security")}</p>
            </div>
          </div>

          <div className="mt-10 flex gap-4">
            <Link to="/pricing" className="text-sm text-brand-primary hover:underline">{t("about.links.pricing")}</Link>
            <Link to="/terms" className="text-sm text-muted-foreground hover:text-foreground">{t("about.links.terms")}</Link>
            <Link to="/privacy" className="text-sm text-muted-foreground hover:text-foreground">{t("about.links.privacy")}</Link>
          </div>
        </motion.div>
      </div>
    </main>
  );
}
